/**
 * Achievements API Types
 * Types for system achievements, collection achievements, and badges
 */

// ===================
// ENUMS
// ===================

export enum SystemAchievementCriteriaType {
  TotalAdventuresCompleted = 1,
  TotalAdventuresFailed = 2,
  TotalSoloAdventuresCompleted = 3,
  TotalGroupAdventuresCompleted = 4,
  UniqueAdventuresCompleted = 5,
  CategoryAdventuresCompleted = 6,
  TotalLocationsVisited = 10,
  TotalGeoCachesFound = 11,
  TotalEventsAttended = 20,
  TotalFriendsMade = 30,
  TotalGroupSessionsLed = 31,
  TotalMessagesSent = 32,
  ConsecutiveDaysActive = 40,
  WeeklyCompletionStreak = 41,
  TopAdventureCompletion = 50,
  PerfectAdventureCompletion = 51,
  SpeedCompletion = 52,
  Custom = 100,
}

// ===================
// SYSTEM ACHIEVEMENT DTOs
// ===================

/** System achievement with user progress (from /api/system-achievements) */
export interface SystemAchievementDto {
  id: string
  name: string
  shortDescription: string
  fullDescription: string | null
  iconUrl: string | null
  criteriaType: SystemAchievementCriteriaType
  threshold: number
  points: number
  isHidden: boolean
  displayOrder: number
  currentValue: number
  completionPercentage: number
  isCompleted: boolean
  completedDate: string | null
  badgeId: string | null
}

/** User's progress on a system achievement (from /api/system-achievements/my-progress) */
export interface SystemAchievementProgressDto {
  achievementId: string
  achievementName: string
  achievementIconUrl: string | null
  threshold: number
  currentValue: number
  completionPercentage: number
  isCompleted: boolean
  completedDate: string | null
  // Streak-specific fields
  currentStreak: number
  bestStreak: number
  lastStreakActivityDate: string | null
}

/** Summary of user's system achievements (from /api/system-achievements/summary) */
export interface UserSystemAchievementSummaryDto {
  totalAchievements: number
  completedCount: number
  totalPoints: number
  earnedPoints: number
  earnedAchievements: SystemAchievementDto[]
}

// ===================
// COLLECTION ACHIEVEMENT DTOs
// ===================

/** User's progress on a collection achievement (from /api/achievements/my-progress/summary) */
export interface CollectionAchievementProgressSummaryDto {
  achievementId: string
  achievementName: string
  description: string | null
  iconUrl: string | null
  completedCount: number
  totalAdventureCount: number
  completionPercentage: number
  isCompleted: boolean
  hasBlockingFailures: boolean
  completedDate: string | null
  collectionId: string | null
  collectionName: string | null
}

/** User's achievement progress (from /api/users/{userId}/achievements) */
export interface UserAchievementProgressDto {
  achievementId: string
  achievementName: string
  description: string | null
  iconUrl: string | null
  completedCount: number
  totalAdventureCount: number
  completionPercentage: number
  isCompleted: boolean
  hasBlockingFailures: boolean
  completedDate: string | null
}

// ===================
// BADGE DTOs
// ===================

/** Badge awarded to user */
export interface BadgeDto {
  id: string
  name: string
  description: string | null
  imageUrl: string | null
  earnedDate: string
  sourceType: 'system' | 'collection'
  sourceId: string | null
  sourceName: string | null
}

// ===================
// COMBINED TYPES FOR UI
// ===================

/** Combined achievement for display in UI (merges system + collection) */
export interface DisplayAchievement {
  id: string
  name: string
  shortDescription: string
  fullDescription: string | null
  iconUrl: string | null
  category: string
  criteriaType: SystemAchievementCriteriaType | null
  currentValue: number
  threshold: number
  completionPercentage: number
  isCompleted: boolean
  completedDate: string | null
  // Streak-specific (only for streak achievements)
  currentStreak?: number
  bestStreak?: number
  lastStreakActivityDate?: string | null
  // Source type
  sourceType: 'system' | 'collection'
}
